import { diag, DiagConsoleLogger, DiagLogLevel } from "@opentelemetry/api";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { FetchInstrumentation } from "@opentelemetry/instrumentation-fetch";
import { registerInstrumentations } from "@opentelemetry/instrumentation";
import { XMLHttpRequestInstrumentation } from "@opentelemetry/instrumentation-xml-http-request";
import { SamplingDecision, WebTracerProvider } from "@opentelemetry/sdk-trace-web";
import { BatchSpanProcessor } from "@opentelemetry/sdk-trace-base";
import type { Sampler, SamplingResult } from "@opentelemetry/sdk-trace-base";
import { ZoneContextManager } from "@opentelemetry/context-zone-peer-dep";
import "zone.js";
import { ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION, SEMRESATTRS_DEPLOYMENT_ENVIRONMENT } from "@opentelemetry/semantic-conventions";
import { W3CTraceContextPropagator } from "@opentelemetry/core";

let providerInstance: WebTracerProvider | null = null;
let telemetryInitialized = false;

const ignoredTelemetryUrlPatterns = [
	/api\/telemetry\/observableEndpoints/,
	/proxy\/api\/telemetry\/observableEndpoints/,
	/telemetry\/v1\/traces/,
];

class TelemetrySampler implements Sampler {
	shouldSample(
		context: any,
		traceId: string,
		spanName: string,
		spanKind: any,
		attributes: any,
		links: any[]
	): SamplingResult {
		return {
			decision: SamplingDecision.RECORD_AND_SAMPLED,
		};
	}

	toString(): string {
		return "TelemetrySampler";
	}
}

export default defineNuxtPlugin(async () => {
	const config = useRuntimeConfig();
	const otelEnabled = config.public.OTEL_ENABLED === "true" || config.public.OTEL_ENABLED === true;

	if (!otelEnabled || typeof window === "undefined") {
		return {
			provide: {
				tracer: null,
			},
		};
	}

	if (!telemetryInitialized) {
		telemetryInitialized = true;
		diag.setLogger(new DiagConsoleLogger(), DiagLogLevel.ERROR);

		const serviceName = config.public.OTEL_SERVICE_NAME || "harpia-wms-frontend";
		const configuredEndpoint = config.public.OTEL_EXPORTER_OTLP_TRACES_ENDPOINT;
		const endpoint = typeof configuredEndpoint === "string" && configuredEndpoint.startsWith("http")
			? configuredEndpoint
			: `${window.location.origin}${configuredEndpoint || "/telemetry/v1/traces"}`;

		const exporter = new OTLPTraceExporter({
			url: endpoint,
			headers: {
				"Content-Type": "application/json",
			},
		});

		const processor = new BatchSpanProcessor(exporter, {
			maxExportBatchSize: 10,
			scheduledDelayMillis: 300,
		});

		const provider = new WebTracerProvider({
			sampler: new TelemetrySampler(),
			spanProcessors: [processor],
			resource: {
				attributes: {
					[ATTR_SERVICE_NAME]: serviceName,
					[ATTR_SERVICE_VERSION]: "1.0.0",
					[SEMRESATTRS_DEPLOYMENT_ENVIRONMENT]: process.env.NODE_ENV || "development",
					"harpia.wms.module": "frontend",
				},
			} as any,
		});

		provider.register({
			contextManager: new ZoneContextManager(),
			propagator: new W3CTraceContextPropagator(),
		});

		const xhrInstrumentation = new XMLHttpRequestInstrumentation({
			propagateTraceHeaderCorsUrls: [
				/http:\/\/localhost.*/,
				/.*\/api\/.*/,
				/.*\/proxy\/.*/,
			],
			ignoreUrls: ignoredTelemetryUrlPatterns,
		});

		xhrInstrumentation.setTracerProvider(provider);
		xhrInstrumentation.enable();

		registerInstrumentations({
			tracerProvider: provider,
			instrumentations: [
				xhrInstrumentation,
				new FetchInstrumentation({
					propagateTraceHeaderCorsUrls: [
						/http:\/\/localhost.*/,
						/.*\/api\/.*/,
						/.*\/proxy\/.*/,
					],
					ignoreUrls: ignoredTelemetryUrlPatterns,
				}),
			],
		});

		providerInstance = provider;

		const flushTelemetry = () => {
			void provider.forceFlush?.().catch(() => undefined);
		};

		window.addEventListener("visibilitychange", () => {
			if (document.visibilityState === "hidden") {
				flushTelemetry();
			}
		});
		window.addEventListener("pagehide", flushTelemetry);
		window.addEventListener("beforeunload", flushTelemetry);

	}

	const serviceName = config.public.OTEL_SERVICE_NAME || "harpia-wms-frontend";

	return {
		provide: {
			tracer: providerInstance ? providerInstance.getTracer(serviceName) : null,
		},
	};
});
